import { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container, Divider, Grid } from "@mui/material";
import { LocaleContext } from "../../routing/LangRouter";
import { scrollToPosition, numberWithCommas } from "../../utils/utility";
import { useTranslation } from "react-i18next";
import axios from "axios";
import BASE_URL from "../../constants/baseUrls";

//import general components
import { BaseButton } from "../../components/common/base-button/base-button";
import FlightCard from "../../components/common/minimal-flight-card/minimal-flight-card";
import HotelCard from "../../components/common/minimal-hotel-card/minimal-hotel-card";
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import SelectedItem from "../../components/common/selected-item/selected-item";
import LoginModal from "../../components/common/login-modal/login-modal";

//import styles
import "./cart.scss";
import { IS_MOBILE } from "../../constants/breakpoints";

//import utils
import { formatPersianDate } from "../../utils/date";
import { flightDataMapper } from "../../utils/flightDataMapper";

function Cart() {
    //general
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight"));
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel"));
    const selectedRooms = JSON.parse(sessionStorage.getItem("selectedRooms"));
    const selectedIntRooms = JSON.parse(sessionStorage.getItem("selectedIntRooms"));
    const planId = JSON.parse(sessionStorage.getItem("planId"));
    const startDatetime = JSON.parse(sessionStorage.getItem("startDatetime"));
    const endDatetime = JSON.parse(sessionStorage.getItem("endDatetime"));
    const { isFlightInternational } = JSON.parse(sessionStorage.getItem("generalInformation"));

    //flight data variables
    const isTwoWay = domesticFlightInformation?.returnDate;
    const readSegment = isFlightInternational || (!isFlightInternational && !!isTwoWay);
    const readReturnSegment = isFlightInternational ? selectedIntFlight : selectedFlight;
    const normalizedDepartureFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.segments);
    const normalizedReturnFlight = flightDataMapper(isTwoWay, selectedFlight, selectedFlight?.return_segments);

    useEffect(() => {
        scrollToPosition(0, 0);
    }, []);

    //total price
    const [totalPrice, setTotalPrice] = useState();
    useEffect(() => {
        let oneWayFlightPrice = 0;
        let twoWayFlightPrice = 0;
        let intFlightPrice = 0;
        let hotelPrice = 0;
        if (selectedFlight && !isTwoWay)
            oneWayFlightPrice =
                selectedFlight?.adult_price * domesticFlightInformation.personCounter.adult +
                selectedFlight?.child_price * domesticFlightInformation.personCounter.child +
                selectedFlight?.infant_price * domesticFlightInformation.personCounter.infant;
        if (selectedFlight && !!isTwoWay) twoWayFlightPrice = selectedFlight?.fares?.total_price;

        const domesticFlightPrice = !!isTwoWay ? twoWayFlightPrice : oneWayFlightPrice;

        if (selectedIntFlight) intFlightPrice = selectedIntFlight?.fares?.total_price;
        if (selectedRooms) {
            hotelPrice = selectedRooms?.reduce((accumulator, currentValue) => accumulator + currentValue.prices.total_price * currentValue.count, 0);
        }
        if (selectedIntRooms) {
            hotelPrice = selectedIntRooms.fare.total;
        }
        setTotalPrice(domesticFlightPrice + intFlightPrice + hotelPrice);
    }, []);

    //handle delete cart
    const handleDelete = name => {
        if (name === "selectedDepartureFlight" || name === "selectedReturnFlight") {
            sessionStorage.removeItem("selectedFlight");
            sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));
            navigate(`/${locale}/itinerary/domestic-flight`);
        }

        if (name === "selectedHotel") {
            sessionStorage.removeItem("selectedHotel");
            sessionStorage.removeItem("selectedRooms");
            sessionStorage.removeItem("selectedIntRooms");
            navigate(`/${locale}/itinerary/hotel`);
        }
    };

    const intFlightData = selectedIntFlight
        ? {
              title: `${t("departureTicket")}  ${intFlightInformation.returnDate ? t("Flight.And Return") : ""}`,
              name: [
                  ...selectedIntFlight?.segments?.map(segment =>
                      locale === "en" ? segment?.departure?.city?.english : segment?.departure?.city?.persian,
                  ),
                  selectedIntFlight?.segments[selectedIntFlight?.segments?.length - 1]?.destination?.city?.persian,
              ],
              date: (
                  <Grid container item xs={12} direction="column">
                      <span>
                          {t("departureDate")}:{" "}
                          {formatPersianDate(selectedIntFlight?.segments?.[0]?.departure_date, {
                              day: "numeric",
                              month: "long",
                              year: undefined,
                              weekday: "long",
                          })}{" "}
                          - {selectedIntFlight?.segments?.[0]?.departure_time.split(":", 2).join(":")}
                      </span>
                      {intFlightInformation.returnDate && (
                          <span>
                              {t("returnDate")}:{" "}
                              {formatPersianDate(selectedIntFlight?.return_segments?.[0]?.departure_date, {
                                  day: "numeric",
                                  month: "long",
                                  year: undefined,
                                  weekday: "long",
                              })}{" "}
                              - {selectedIntFlight?.return_segments?.[0]?.departure_time.split(":", 2).join(":")}
                          </span>
                      )}
                  </Grid>
              ),
              type: "intFlight",
              details: selectedIntFlight,
              route: "international-flight",
          }
        : null;

    //handle submit
    const handleSubmit = async () => {
        const data = {
            accommodation: selectedHotel?.id ?? null,
            accommodation_check_in: hotelInformation.checkIn,
            accommodation_check_out: hotelInformation.checkOut,
            city: isFlightInternational ? intFlightInformation?.destination?.id : domesticFlightInformation?.destination?.id,
            flight_number: readSegment ? selectedIntFlight?.segments[0]?.flight_number : selectedFlight?.flight_number,
            flight_departure: readSegment ? selectedIntFlight?.segments[0]?.departure?.city?.persian : selectedFlight?.departure_name,
            flight_departure_terminal: readSegment ? selectedIntFlight?.segments[0]?.departure?.terminal?.name : selectedFlight?.departure_terminal,
            flight_destination: readSegment ? selectedIntFlight?.segments[0]?.destination?.city?.persian : selectedFlight?.destination_name,
            flight_destination_terminal: readSegment
                ? selectedIntFlight?.segments[0]?.destination.terminal.name
                : selectedFlight?.destination_terminal,
            flight_departure_date: readSegment ? selectedIntFlight?.segments[0]?.departure_date : selectedFlight?.departure_date,
            flight_destination_date: "",
            flight_duration: readSegment ? selectedIntFlight?.segments[0]?.flight_duration : selectedFlight?.flight_duration,
            flight_airline: readSegment ? selectedIntFlight?.segments[0]?.airline?.persian : selectedFlight?.airline_persian,
            flight_cabin: readSegment ? selectedIntFlight?.cabin?.english : selectedFlight?.cobin,
            flight_cabin_persian: readSegment ? selectedIntFlight?.cabin?.persian : selectedFlight?.cobin_persian,
            flight_type_persian: readSegment ? "" : selectedFlight?.type_persian,
            flight_type: readSegment ? "" : selectedFlight?.type,
            flight_price: readSegment ? selectedIntFlight?.fares?.adult?.price : selectedFlight?.adult_price,
            flight_airline_logo: readSegment ? selectedIntFlight?.segments[0]?.airline?.image : selectedFlight?.airline_logo,
            flight_tag: isFlightInternational ? selectedIntFlight?.tag : selectedFlight?.tag,
            flight_departure_time: readSegment ? selectedIntFlight?.segments[0]?.departure_time : selectedFlight?.departure_time,
            plan: planId,
            description: "",
            start_datetime: startDatetime,
            end_datetime: endDatetime,
            flight_number2: readReturnSegment?.return_segments[0]?.flight_number,
            flight_departure2: readReturnSegment?.return_segments[0]?.departure?.city?.persian,
            flight_departure_terminal2: "",
            flight_destination2: readReturnSegment?.return_segments[0]?.destination?.city?.persian,
            flight_destination_terminal2: readReturnSegment?.return_segments[0]?.destination?.terminal.name,
            flight_departure_date2: readReturnSegment?.return_segments[0]?.departure_date,
            flight_destination_date2: "",
            flight_duration2: readReturnSegment?.return_segments[0]?.flight_duration,
            flight_airline2: readReturnSegment?.return_segments[0]?.airline?.persian,
            flight_cabin2: readReturnSegment?.cabin?.english,
            flight_cabin_persian2: readReturnSegment?.cabin?.persian,
            flight_type_persian2: "",
            flight_type2: "",
            flight_price2: readReturnSegment?.fares?.adult?.price,
            flight_airline_logo2: readReturnSegment?.return_segments[0]?.airline?.image,
            flight_tag2: isFlightInternational ? selectedIntFlight?.tag : selectedFlight?.tag,
            flight_departure_time2: readReturnSegment?.return_segments[0]?.departure_time,
        };

        if (!!accessToken) {
            try {
                const response = await axios.post(`${BASE_URL}/itinerary/save_plan/`, data, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
            } catch (error) {
                if (error.response.status === 401) {
                    localStorage.clear();
                    setSearchParams({ action: "login" });
                } else {
                    // toast.error("درخواست با خطا مواجه شد", { toastId: "custom-id-yes" });
                }
            }

            if (selectedFlight) {
                navigate(`/${locale}/flight-passengers`);
            }

            if (selectedIntFlight) {
                navigate(`/${locale}/int-flight-passengers`);
            }

            if (!selectedFlight && !selectedIntFlight && selectedHotel && selectedRooms) {
                //by (!selectedIntFlight), it considers when we select International Flight with Domestic hotels of grs
                navigate(`/${locale}/hotel-passengers`);
            }
            if (!selectedFlight && !selectedIntFlight && selectedHotel && selectedIntRooms) {
                navigate(`/${locale}/int-hotel-passengers`);
            }
        } else {
            setSearchParams({ action: "login" });
        }
    };

    //handle modal change
    // const [openLogin, setOpenLogin] = useState(false);
    // useEffect(() => {
    //     if (searchParams.get("action") === "login") setOpenLogin(true);
    //     else setOpenLogin(false);
    // }, [searchParams]);

    //mobile functions
    const goToBack = () => {
        navigate(`/${locale}/itinerary/itinerary-page`, { replace: true });
    };

    return (
        <Grid className="cart" container>
            <MobileHeader title={t("cart")} justifyContent="center" handleClick={goToBack} />

            <Container maxWidth="xl" sx={{ p: { xs: "0", md: "24px" } }}>
                <Grid
                    container
                    item
                    xs={12}
                    rowGap={{ xs: 0, md: 4 }}
                    justifyContent={{ xs: "center", md: "flex-end" }}
                    mb={{ xs: 8, md: 0 }}
                    mt={IS_MOBILE ? 6 : 0}
                >
                    <Grid container item xs={12} rowGap={{ xs: 0, md: 2 }} justifyContent="space-between">
                        {!!selectedFlight || selectedFlight?.segments?.lenght > 0 ? (
                            <Grid className="cart__flight-item" container item xs={12} md={5.8} rowGap={{ xs: 1, md: 3 }}>
                                <Grid container item xs={12} justifyContent="space-between" alignItems="center" order={{ xs: 2, md: 1 }}>
                                    <span className="cart__title">{t("departureTicket")}</span>
                                    <Divider sx={{ width: { xs: "100%", md: "85%", xl: "80%" } }} />
                                </Grid>
                                <FlightCard
                                    isClickable={false}
                                    flightItem={normalizedDepartureFlight}
                                    type="cart"
                                    name="selectedDepartureFlight"
                                    handleDelete={handleDelete}
                                />
                            </Grid>
                        ) : (
                            ""
                        )}
                        {!!isTwoWay && !!selectedFlight ? (
                            <Grid className="cart__flight-item" container item xs={12} md={5.8} rowGap={{ xs: 1, md: 3 }}>
                                <Grid container item xs={12} justifyContent="space-between" alignItems="center" order={{ xs: 2, md: 1 }}>
                                    <span className="cart__title">{t("returnTicket")}</span>
                                    <Divider sx={{ width: { xs: "100%", md: "82%", xl: "80%" } }} />
                                </Grid>
                                <FlightCard
                                    isClickable={false}
                                    flightItem={normalizedReturnFlight}
                                    type="cart"
                                    name="selectedReturnFlight"
                                    handleDelete={handleDelete}
                                />
                            </Grid>
                        ) : (
                            ""
                        )}
                    </Grid>

                    {selectedIntFlight ? (
                        <Grid container item xs={11} md={12} rowGap={1} columnGap={1} mt={IS_MOBILE ? 3 : 0}>
                            <SelectedItem data={intFlightData} />
                        </Grid>
                    ) : (
                        ""
                    )}

                    {selectedIntRooms || selectedRooms ? (
                        <Grid container item xs={12} rowGap={{ xs: 1, md: 3 }}>
                            <Grid container item xs={12} display={{ xs: "none", md: "flex" }} justifyContent="space-between" alignItems="center">
                                <span className="cart__title">{t("hotel")}</span>
                                <Divider width="96%" />
                            </Grid>
                            <HotelCard
                                hotelItem={selectedHotel}
                                selectedRooms={selectedRooms}
                                selectedIntRooms={selectedIntRooms}
                                name="selectedHotel"
                                handleDelete={handleDelete}
                            ></HotelCard>
                        </Grid>
                    ) : (
                        ""
                    )}

                    <Grid className="cart__submit" container item xs={12} rowGap={1} justifyContent="center">
                        <Grid container item xs={11} md={12} justifyContent="space-between" alignItems="center">
                            <Grid className="cart__price" component="span" container item xs="auto">
                                {t("total")} {t("price")}
                            </Grid>
                            <Divider sx={{ width: { xs: "30%", md: "80%" } }} />
                            <Grid className="cart__price" component="span" container item xs="auto">
                                {numberWithCommas(totalPrice)} {t("currency")}
                            </Grid>
                        </Grid>
                        <Grid container item xs={11} md={1.5}>
                            <BaseButton text={t("Confirm and Continue")} color="green" size="large" radius="radius-10" handleClick={handleSubmit} />
                        </Grid>
                    </Grid>
                </Grid>
            </Container>

            {/* <LoginModal open={openLogin} setOpen={setOpenLogin} /> */}
        </Grid>
    );
}

export default Cart;
